import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, exhaustMap, expand, map, reduce, switchMap, tap } from 'rxjs/operators';
import { BaseListQuery } from '../../features/base-management/models';
import {
  BaseListPage,
  toListBasesParams,
} from '../../features/base-management/utils/base-list-query.util';
import { PageMeta } from '../../api/scheduler/models/page-meta';
import { profileImageUploadError } from '../../features/base-management/utils/base-profile-image';
import { reservedColorsFromLegends } from '../../features/base-management/utils/reserved-colors';
import { BaseManagementApiService } from '../../features/base-management/services/base-management-api.service';
import { UiActionToastService } from '../../shared/components/ui/ui-action-toast/ui-action-toast.service';
import {
  GENERIC_USER_ERROR,
  userFacingApiError,
} from '../master-schedule/master-schedule-user-errors';
import { ToastActions } from '../toast/toast.actions';
import { BaseManagementActions } from './base-management.actions';

/** Page size used while walking the Base Lead picker, which is not shown one page at a time. */
const LEADS_PAGE_SIZE = 100;
const COLOR_IN_USE_MESSAGE =
  'This colour is already used by another Base. Please choose a different one.';
const COLOR_INVALID_MESSAGE = 'Choose a valid colour.';
const DISPLAY_CODE_TOO_LONG_MESSAGE = 'Display Code must be 16 characters or fewer.';
const CODE_IN_USE_MESSAGE = 'This Base Code is already in use. Please choose a different code.';
const BASE_INVALID_MESSAGE =
  'Please check the Base details. Code must be 3 letters, and a Base Lead is required.';
const SHIFTS_INVALID_MESSAGE =
  'Active shifts cannot overlap, and a shift cannot end at the time it starts.';
const SHIFT_NAME_INVALID_MESSAGE =
  'The shift name must be unique on this base and at most 50 characters.';

/** Fetches every page of a paged list by following the page metadata, then joins the rows. */
function fetchAllPages<T>(
  fetchPage: (page: number) => Observable<{ page?: PageMeta; data?: T[] }>
): Observable<T[]> {
  return fetchPage(0).pipe(
    expand((response, index) => {
      const totalPages = response.page?.totalPages ?? 1;
      const hasMore = response.page?.last === false && index + 1 < totalPages;
      return hasMore ? fetchPage(index + 1) : EMPTY;
    }),
    reduce((rows: T[], response) => [...rows, ...(response.data ?? [])], [])
  );
}

/** The `code` (or, failing that, the `message`) of an API error body, e.g. `errors.baseShiftNameInvalid`. */
function apiErrorKey(error: HttpErrorResponse): string | null {
  const body = error.error as { code?: unknown; message?: unknown } | null;
  for (const value of [body?.code, body?.message]) {
    if (typeof value === 'string' && value.startsWith('errors.')) return value;
  }
  return null;
}

/** Friendly, fixed wording for the Base save conflicts; anything else uses the generic message. */
function baseSaveError(error: unknown, invalidMessage: string): string {
  if (error instanceof HttpErrorResponse) {
    const key = apiErrorKey(error);
    if (key === 'errors.baseColorInUse') return COLOR_IN_USE_MESSAGE;
    if (key === 'errors.baseColorInvalid') return COLOR_INVALID_MESSAGE;
    if (key === 'errors.baseDisplayCodeTooLong') return DISPLAY_CODE_TOO_LONG_MESSAGE;
    if (error.status === 409) return CODE_IN_USE_MESSAGE;
    if (error.status === 422) return invalidMessage;
  }
  return userFacingApiError(error);
}

/** Wording for a rejected shift save. A shift conflict is never a Base Code conflict. */
function shiftSaveError(error: unknown): string {
  if (error instanceof HttpErrorResponse && error.status === 422) {
    if (apiErrorKey(error) === 'errors.baseShiftNameInvalid') return SHIFT_NAME_INVALID_MESSAGE;
    const message = userFacingApiError(error);
    return message === GENERIC_USER_ERROR || message.startsWith('errors.')
      ? SHIFTS_INVALID_MESSAGE
      : message;
  }
  return userFacingApiError(error);
}

@Injectable()
export class BaseManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly api = inject(BaseManagementApiService);
  private readonly actionToast = inject(UiActionToastService);

  loadAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadAccess),
      switchMap(() =>
        this.api.getAccess().pipe(
          map((response) =>
            BaseManagementActions.loadAccessSuccess({
              allowed: response.data?.allowed ?? false,
              canWrite: response.data?.canWrite ?? false,
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.loadAccessFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  loadBases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadBases),
      switchMap(({ query }) =>
        this.loadBasePage(query).pipe(
          map((page) => BaseManagementActions.loadBasesSuccess(page)),
          catchError((error) =>
            of(BaseManagementActions.loadBasesFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  loadBase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadBase),
      switchMap(({ id }) =>
        this.api.get(id).pipe(
          map((response) => BaseManagementActions.loadBaseSuccess({ base: response.data! })),
          catchError((error) =>
            of(BaseManagementActions.loadBaseFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  loadLookups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadLookups),
      switchMap(() =>
        forkJoin({
          leads: fetchAllPages((page) => this.api.listLeads({ page, size: LEADS_PAGE_SIZE })),
          timeZones: this.api.listTimeZones(),
        }).pipe(
          map(({ leads, timeZones }) =>
            BaseManagementActions.loadLookupsSuccess({
              leads,
              timeZones: timeZones.data ?? [],
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.loadLookupsFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  loadBaseColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadBaseColors),
      switchMap(() =>
        this.api.listColors().pipe(
          map((response) =>
            BaseManagementActions.loadBaseColorsSuccess({ colors: response.data ?? [] })
          ),
          // The server still rejects a taken colour on save, so a failed lookup is not shown.
          catchError(() => of(BaseManagementActions.loadBaseColorsFailure()))
        )
      )
    )
  );

  loadReservedColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadReservedColors),
      switchMap(() =>
        this.api.getScheduleLegends().pipe(
          map((response) =>
            BaseManagementActions.loadReservedColorsSuccess({
              colors: reservedColorsFromLegends(response.data),
            })
          ),
          // Without the legends (e.g. no Master Schedule access) only other Bases' colours are blocked.
          catchError(() => of(BaseManagementActions.loadReservedColorsFailure()))
        )
      )
    )
  );

  /** The schedule colours rarely change, so they load once with the other lookups. */
  refreshReservedColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadLookups),
      map(() => BaseManagementActions.loadReservedColors())
    )
  );

  /** Reload the colours in use at start and after every Base write, so a taken colour shows at once. */
  refreshBaseColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BaseManagementActions.loadLookups,
        BaseManagementActions.createBaseSuccess,
        BaseManagementActions.updateBaseSuccess
      ),
      map(() => BaseManagementActions.loadBaseColors())
    )
  );

  createBase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.createBase),
      switchMap(({ request, imageFile }) =>
        this.api.create(request).pipe(
          switchMap((response) => {
            const base = response.data!;
            if (!imageFile) return of(BaseManagementActions.createBaseSuccess({ base }));
            // The Base exists now; a failed image upload must not undo or hide that.
            return this.api.uploadProfileImage(base.id, imageFile).pipe(
              map((image) =>
                BaseManagementActions.createBaseSuccess({
                  base: { ...base, profileImageUrl: image.data?.profileImageUrl ?? null },
                })
              ),
              catchError((error) =>
                of(
                  BaseManagementActions.createBaseSuccess({ base }),
                  ToastActions.showToast({
                    severity: 'warn',
                    summary: 'Profile image not uploaded',
                    detail: `${profileImageUploadError(error)} The Base was created; add the image from its details page.`,
                  })
                )
              )
            );
          }),
          catchError((error) =>
            of(
              BaseManagementActions.saveFailure({
                error: baseSaveError(error, BASE_INVALID_MESSAGE),
              })
            )
          )
        )
      )
    )
  );

  uploadProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.uploadProfileImage),
      switchMap(({ id, file }) =>
        this.api.uploadProfileImage(id, file).pipe(
          map((response) =>
            BaseManagementActions.uploadProfileImageSuccess({
              id,
              profileImageUrl: response.data?.profileImageUrl ?? null,
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.saveFailure({ error: profileImageUploadError(error) }))
          )
        )
      )
    )
  );

  deleteProfileImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.deleteProfileImage),
      switchMap(({ id }) =>
        this.api.deleteProfileImage(id).pipe(
          map(() => BaseManagementActions.deleteProfileImageSuccess({ id })),
          catchError((error) =>
            of(BaseManagementActions.saveFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  profileImageToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BaseManagementActions.uploadProfileImageSuccess,
        BaseManagementActions.deleteProfileImageSuccess
      ),
      map((action) =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Profile image',
          detail:
            action.type === BaseManagementActions.uploadProfileImageSuccess.type
              ? 'The profile image was updated.'
              : 'The profile image was removed.',
        })
      )
    )
  );

  updateBase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.updateBase),
      switchMap(({ id, request }) =>
        this.api.update(id, request).pipe(
          map((response) => BaseManagementActions.updateBaseSuccess({ base: response.data! })),
          catchError((error) =>
            of(
              BaseManagementActions.saveFailure({
                error: baseSaveError(error, BASE_INVALID_MESSAGE),
              })
            )
          )
        )
      )
    )
  );

  createShift$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.createShift),
      // exhaustMap, not switchMap: a second Save must not cancel a create that may already have
      // reached the server and so add the same shift twice.
      exhaustMap(({ id, request }) =>
        this.api.createShift(id, request).pipe(
          map((response) =>
            BaseManagementActions.createShiftSuccess({
              base: response.data!,
              shiftName: request.name,
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.saveFailure({ error: shiftSaveError(error) }))
          )
        )
      )
    )
  );

  updateShifts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.updateShifts),
      switchMap(({ id, request }) =>
        this.api.updateShifts(id, request).pipe(
          map((response) => BaseManagementActions.updateShiftsSuccess({ base: response.data! })),
          catchError((error) =>
            of(BaseManagementActions.saveFailure({ error: shiftSaveError(error) }))
          )
        )
      )
    )
  );

  /** One request for the page on screen. Status and shift filters are applied by the API. */
  private loadBasePage(query: BaseListQuery): Observable<BaseListPage> {
    return this.api.list(toListBasesParams(query)).pipe(
      map((response) => ({
        bases: response.data ?? [],
        totalElements: response.page?.totalElements ?? response.data?.length ?? 0,
        totalPages: response.page?.totalPages ?? 1,
      }))
    );
  }

  createdToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.createBaseSuccess),
      map(({ base }) =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Base created',
          detail: `${base.name} (${base.code}) was added with its Day, Night and 3rd shifts.`,
        })
      )
    )
  );

  updatedToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.updateBaseSuccess),
      map(({ base }) =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Base updated',
          detail: `${base.name} was saved.`,
        })
      )
    )
  );

  shiftCreatedToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.createShiftSuccess),
      map(({ base, shiftName }) =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Shift added',
          detail: `${shiftName} was added to ${base.name}.`,
        })
      )
    )
  );

  shiftsUpdatedToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.updateShiftsSuccess),
      map(({ base }) =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Shifts updated',
          detail: `Shift timings for ${base.name} were saved.`,
        })
      )
    )
  );

  loadCrew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.loadCrew),
      switchMap(({ baseId, startDate, endDate, search, rank, rotation, crewStatus, page, size }) =>
        this.api.listBaseCrewUsers({ id: baseId, startDate, endDate, search, rank, rotation, crewStatus, page, size }).pipe(
          map((response) =>
            BaseManagementActions.loadCrewSuccess({
              crew: response.data ?? [],
              totalElements: response.page?.totalElements ?? 0,
              totalPages: response.page?.totalPages ?? 1,
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.loadCrewFailure({
              error: userFacingApiError(error),
              forbidden: error instanceof HttpErrorResponse && error.status === 403,
            }))
          )
        )
      )
    )
  );

  assignCrewLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.assignCrewLead),
      switchMap(({ baseId, crewId }) =>
        this.api.setBaseCrewLead(baseId, { crewId }).pipe(
          map((response) =>
            BaseManagementActions.assignCrewLeadSuccess({
              baseId,
              newLead: response.data!.newLead,
              previousCrewId: response.data!.previousCrewId ?? null,
            })
          ),
          catchError((error) =>
            of(BaseManagementActions.assignCrewLeadFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  assignCrewLeadSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseManagementActions.assignCrewLeadSuccess),
        tap(({ newLead, baseId, previousCrewId }) => {
          const ref = this.actionToast.show({
            message: `${newLead.fullName} is now the Base Lead.`,
            actionLabel: 'Undo',
          });
          ref.onAction().subscribe(() => {
            this.store.dispatch(
              BaseManagementActions.revertCrewLead({ baseId, previousCrewId })
            );
          });
        })
      ),
    { dispatch: false }
  );

  revertCrewLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.revertCrewLead),
      switchMap(({ baseId, previousCrewId }) =>
        this.api.revertBaseCrewLead(baseId, { previousCrewId }).pipe(
          map(() => BaseManagementActions.revertCrewLeadSuccess({ baseId })),
          catchError((error) =>
            of(BaseManagementActions.revertCrewLeadFailure({ error: userFacingApiError(error) }))
          )
        )
      )
    )
  );

  revertCrewLeadSuccessToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.revertCrewLeadSuccess),
      map(() =>
        ToastActions.showToast({
          severity: 'success',
          summary: 'Base Lead reverted',
          detail: 'The Base Lead change has been undone.',
        })
      )
    )
  );

  revertCrewLeadReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseManagementActions.revertCrewLeadSuccess),
      map(({ baseId }) => BaseManagementActions.loadBase({ id: baseId }))
    )
  );

  failureToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BaseManagementActions.loadBasesFailure,
        BaseManagementActions.loadBaseFailure,
        BaseManagementActions.loadLookupsFailure,
        BaseManagementActions.loadCrewFailure,
        BaseManagementActions.assignCrewLeadFailure,
        BaseManagementActions.revertCrewLeadFailure,
        BaseManagementActions.saveFailure
      ),
      map(({ error }) =>
        ToastActions.showToast({
          severity: 'error',
          summary: 'Base Management',
          detail: error,
        })
      )
    )
  );
}
